import type { MsgKey } from "./i18n";
import type { I18nText } from "./locales";

/**
 * 通关奖励。字段固定，来自需求脑图「通关配置 · 通关奖励配置」：
 * money / spin / 物品卡 / idol 卡 / 拼图碎片 / 活动道具（多选）。
 *
 * 用「一关一条记录、内含多个奖励项」而不是「一关多条记录」：
 * 一关的奖励是一个整体，拆成多行后「这关到底发了什么」要靠人脑聚合。
 */
/**
 * 后五种（shield / free / wildcard / gems / title）是任务 / 成就补进来的，
 * 与服务端 game/reward-kinds.ts 的 REWARD_KINDS 一一对应。
 *
 * **活跃点（points）不在这里**：V1.0 起它是任务条目上的独立字段，
 * 在弹窗里有自己的数字框，不是一种奖励项。
 */
export type RewardKind =
  | "money" | "spin" | "itemCard" | "idolCard" | "puzzle" | "actItem"
  | "shield" | "free" | "wildcard" | "gems" | "title" | "frame";

/** 任务 / 成就的奖励项编辑器可选的全集（通关奖励页仍只用前六种） */
export const ALL_KINDS: RewardKind[] = ["money", "spin", "itemCard", "idolCard", "puzzle", "actItem", "shield", "free", "wildcard", "gems", "title", "frame"];

// 列头是短标签，说明文字挂在旁边的 ? 里。两个常量原本都指向 .hint，
// 结果 6 个表头全变成一整句说明，表头撑到 200px 高。
export const REWARD_LABEL: Record<RewardKind, MsgKey> = {
  money: "rw.money",
  spin: "rw.spin",
  itemCard: "rw.itemCard",
  idolCard: "rw.idolCard",
  puzzle: "rw.puzzle",
  actItem: "rw.actItem",
  shield: "rw.shield",
  free: "rw.free",
  wildcard: "rw.wildcard",
  gems: "rw.gems",
  title: "rw.title",
  frame: "rw.frame",
};

export const REWARD_HINT: Partial<Record<RewardKind, MsgKey>> = {
  money: "rw.money.hint",
  spin: "rw.spin.hint",
  itemCard: "rw.itemCard.hint",
  idolCard: "rw.idolCard.hint",
  puzzle: "rw.puzzle.hint",
  actItem: "rw.actItem.hint",
  shield: "rw.shield.hint",
  gems: "rw.gems.hint",
  title: "rw.title.hint",
  frame: "rw.frame.hint",
};

/** 需要选具体对象的奖励类型；其余只填数量 */
export const NEEDS_REF: RewardKind[] = ["itemCard", "idolCard", "actItem", "title", "frame"];

/** 没有「数量」概念的类型：不显示数量框，服务端按 1 发（重复获得记 0） */
export const NO_QTY: RewardKind[] = ["title", "frame"];

export interface RewardItem {
  kind: RewardKind;
  /** 引用的卡牌 / 道具标识；money、spin、拼图碎片为空 */
  ref?: string | null;
  qty: number | null;
  /**
   * 只在共享奖励（成就、活动档位）的 money 项上有：「几骰」系数 q × sn/sd，十进制串，不经小数。
   * qty 是旧口径的 B 数；按当前生效版本口径编辑其中一份，另一份原样保留（docs/economy-g-model.md §2.4）
   */
  q?: string | null;
  sn?: string | null;
  sd?: string | null;
}

export interface Reward {
  id: string;
  buildingSystem?: "levels" | "pettown";
  levelId: number;
  name: string;
  i18n: I18nText;
  items: RewardItem[];
}

/**
 * 活动道具：`ref` 存的是稳定 id（服务端按 `act_<ref>` 记数），**名字走 i18n**。
 *
 * 之前是把 i18n 键本身当 ref 存（rw.theme1）——名字一改，库里的 ref 就成了另一个道具。
 * 任务成就 V1.0 铺进来的建材包 / 线索 / 荧光棒 / 卡包这些都是待定名词（还没有真资产键），
 * 用稳定 id 占位，名词定了只改这里的展示名，配置行不用重配。
 * rw.theme1..3 保留是为了老数据还能显示，新配置别再选它们。
 */
export const ACT_ITEM_LABEL: Record<string, MsgKey> = {
  material_small: "act.materialSmall",
  material_large: "act.materialLarge",
  card_pack: "act.cardPack",
  card_pack_premium: "act.cardPackPremium",
  clue: "act.clue",
  glowstick: "act.glowstick",
  adopt_ticket_frag: "act.adoptTicketFrag",
  bouquet: "act.bouquet",
  letter: "act.letter",
  idol_voice: "act.idolVoice",
  spin_tree_boost: "act.spinTreeBoost",
  sr_pet_box: "act.srPetBox",
  album_limited_card: "act.albumLimitedCard",
  ur_adopt_ticket: "act.urAdoptTicket",
  "rw.theme1": "rw.theme1",
  "rw.theme2": "rw.theme2",
  "rw.theme3": "rw.theme3",
};
/**
 * 运营在「副玩法配置 → 活动道具 → 活动道具管理」配的名词表（页面拉到后登记进来）。
 * 下拉选项 = 名词表里的 ref ∪ 上面的静态名单；显示名先看名词表（按界面语言），再看静态文案，最后原样显示 id。
 */
let actItemCatalog: Array<{ ref: string; name: Record<string, string> }> = [];
export function setActItemCatalog(items: Array<{ ref: string; name: Record<string, string> }>): void { actItemCatalog = items; }
export const actItemOptions = (): string[] => [...new Set([...actItemCatalog.map((a) => a.ref), ...Object.keys(ACT_ITEM_LABEL)])];
export const ACT_ITEMS: string[] = Object.keys(ACT_ITEM_LABEL);

/**
 * 奖励项显示名按哪个语言取。App 在渲染时设一次，下面几个 label 都读它。
 *
 * 为什么是模块级而不是逐个传参：这几个 label 被四个弹窗的下拉调用，逐个把 locale 串下去
 * 会在每个组件上挂一个只为显示用的参数。写死「优先简中」才是真问题——英文界面下
 * 运营看到的是中文名，跟同一行别的列对不上。
 */
let uiLocale = "zh-CN";
export function setRewardLocale(locale: string): void { uiLocale = locale; }
/** 多语言取值：当前界面语言 → 英文 → 任意一个非空 */
const pick = (m: Record<string, string> | undefined): string | undefined =>
  m ? m[uiLocale] || m.en || Object.values(m).find(Boolean) : undefined;

/** 下拉与摘要共用：认识的 ref 显示名字，不认识的原样显示 id（别把它藏起来，藏了就查不到配错的行） */
export const actItemLabel = (ref: string, t: (k: MsgKey) => string): string => {
  // 运营配的名词表优先（按界面语言），其次静态文案，最后原样显示 id
  const configured = pick(actItemCatalog.find((a) => a.ref === ref)?.name);
  if (configured) return configured;
  const k = ACT_ITEM_LABEL[ref];
  return k ? t(k) : ref;
};

/**
 * 称号名词表。奖励项 `{kind:"title", ref}` 与「最高档称号」下拉都显示它——
 * 不登记的话下拉里是一串 `t_h01`，运营根本认不出是哪个称号。
 */
let titleCatalog: Array<{ id: string; name: Record<string, string> }> = [];
export function setTitleCatalog(items: Array<{ id: string; name: Record<string, string> }>): void { titleCatalog = items; }
/** 认识的称号显示名字，不认识的原样显示 id（停用 / 已删的引用要看得见） */
export const titleLabel = (id: string): string => pick(titleCatalog.find((x) => x.id === id)?.name) || id;

/**
 * 头像框名词表：来自当前版本的「头像框」页（App 里 frames 变了就登记一次）。
 * 奖励项 `{kind:"frame", ref}` 的下拉与摘要按它显示名字，不认识的原样显示 id。
 */
let frameCatalog: Array<{ id: string; name: Record<string, string>; enabled: boolean }> = [];
export function setFrameCatalog(items: Array<{ id: string; name: Record<string, string>; enabled: boolean }>): void { frameCatalog = items; }
/** 下拉只给启用的；已配进奖励但后来停用的 ref 由各弹窗自己补一项，别让它凭空消失 */
export const frameOptions = (): string[] => frameCatalog.filter((f) => f.enabled).map((f) => f.id);
export const frameLabel = (id: string): string => pick(frameCatalog.find((x) => x.id === id)?.name) || id;

/**
 * 卡牌名词表：引用值 → 界面语言下的显示名。来自本版卡包（App 里卡包变了就登记一次）。
 *
 * 卡类奖励的 ref **必须存基准语言名**（`Today's Outfit · Sunglasses`）或编号：服务端
 * resolveCardRef 只认这两种。2026-09-11 起下拉直接拿界面语言名当 value，中文界面下存进去的是
 * 「今日穿搭 · 墨镜」，发布被 ITEM_REF_EXISTS 拒，运营重选一遍存的还是它（a0=1201 v130）。
 * 所以 value 与显示分开：value 是基准名，显示查这张表。
 */
let cardCatalog: Map<string, string> | null = null;
/** 服务端解析得到的引用：停用的包、空包不算（与 resolveCardRef 同口径），显示名照样查得到 */
let cardValid: Set<string> | null = null;
/** 卡牌选取弹窗按它筛：编号 / 卡名 / 卡包 / 状态（禅道 9081）。键是下拉的 value（cardRef） */
export interface CardPickRow {
  ref: string; packId: string; packCode: string; packName: string; packBase: string;
  cardCode: string; cardName: string; cardBase: string; rarity: number; image: string; packDisabled: boolean;
}
let cardRows: Map<string, CardPickRow> = new Map();
export const cardPickRow = (ref: string): CardPickRow | undefined => cardRows.get(ref);
export function setCardCatalog(packs: Array<{ id: string; code?: string | null; name: string; i18n: I18nText; disabled?: boolean | null; cards: Array<{ code?: string | null; name: string; i18n: I18nText; rarity?: number | null; image?: string | null }> }>): void {
  const m = new Map<string, string>();
  const ok = new Set<string>();
  const rows = new Map<string, CardPickRow>();
  for (const p of packs) {
    const pn = pick(p.i18n) || p.name;
    const usable = !p.disabled && p.cards.length > 0;
    // 整包引用（集齐奖励里的 `Drinks`）：包 id / 编号 / 基准名都认
    for (const k of [p.id, p.code, p.name]) if (k) { m.set(k, pn); if (usable) ok.add(k); }
    for (const c of p.cards) {
      const cn = pick(c.i18n) || c.name;
      const label = `${pn} · ${cn}`;
      for (const k of [cardRef(p, c), c.code]) if (k) { m.set(k, label); if (usable) ok.add(k); }
      rows.set(cardRef(p, c), {
        ref: cardRef(p, c), packId: p.id, packCode: p.code ?? "", packName: pn, packBase: p.name,
        cardCode: c.code ?? "", cardName: cn, cardBase: c.name, rarity: Number(c.rarity ?? 0), image: c.image ?? "", packDisabled: !!p.disabled,
      });
    }
  }
  cardCatalog = m;
  cardValid = ok;
  cardRows = rows;
}
/** 卡牌下拉的 value：基准语言名，与服务端 resolveCardRef 同口径 */
export const cardRef = (p: { name: string }, c: { name: string }): string => `${p.name} · ${c.name}`;
/** 物品卡「随机物品」（与服务端 game/card-ref.ts RANDOM_ITEM_REF 同值）：全部物品卡按权重抽一张，数量照填。卡包集齐奖励、通关奖励与任务成就里可选 */
export const RANDOM_ITEM_REF = "*";
/** 服务端解析不到、发布会被拒的卡类引用（名词表还没登记时不判） */
export const isStaleCardRef = (kind: RewardKind, ref: string): boolean =>
  (kind === "itemCard" || kind === "idolCard") && !(kind === "itemCard" && ref === RANDOM_ITEM_REF)
  && cardCatalog != null && cardCatalog.size > 0 && !cardValid?.has(ref);
const cardLabel = (ref: string): string => cardCatalog?.get(ref) ?? ref;

/**
 * 奖励项引用对象的显示名（下拉与摘要共用）：活动道具 / 称号 / 头像框 / 卡牌各查自己的名词表，其余原样。
 * 失效的卡牌引用带上「已失效」：它和正确那项的显示名可能一模一样（界面语言名），不标出来运营看不出要重选
 */
export const refLabel = (kind: RewardKind, ref: string, t: (k: MsgKey) => string): string =>
  kind === "itemCard" && ref === RANDOM_ITEM_REF ? t("reward.randomItem")
  : kind === "actItem" ? actItemLabel(ref, t) : kind === "title" ? titleLabel(ref) : kind === "frame" ? frameLabel(ref)
  : isStaleCardRef(kind, ref) ? `${ref}${t("reward.refStale")}`
  : kind === "itemCard" || kind === "idolCard" ? cardLabel(ref) : ref;

/**
 * 一串奖励项 → 一句话（「骰子×20 + 御守×2」），给列表摘要用。
 * money / spin 用策划看的中文名（通关奖励页列头刻意是英文字段名，那是另一回事）；
 * 称号没有数量；卡类和称号带上引用对象，看得出发的是哪一个。
 */
export function summarizeItems(
  items: RewardItem[], t: (k: MsgKey) => string,
  /** 共享奖励（成就、活动档位）当前生效版本的金额口径：2 = 金币显示几骰系数 q × 刻度，不显示 B 数 */
  sharedModel?: 1 | 2,
): string {
  const dice = (i: RewardItem) => sharedModel === 2 && i.kind === "money";
  return items
    .filter((i) => NO_QTY.includes(i.kind) || (dice(i) ? !!i.q && i.q !== "0" : i.qty != null && Number(i.qty) > 0))
    .map((i) => {
      const name = i.kind === "actItem" && i.ref ? actItemLabel(i.ref, t)
        : i.kind === "title" && i.ref ? titleLabel(i.ref)
        : i.kind === "frame" && i.ref ? frameLabel(i.ref)
        : (i.kind === "itemCard" || i.kind === "idolCard") && i.ref ? `${t(REWARD_LABEL[i.kind])}·${refLabel(i.kind, i.ref, t)}`
        : i.kind === "money" ? t("ach.kind.money")
        : i.kind === "spin" ? t("ach.kind.spin")
        : i.ref ? `${t(REWARD_LABEL[i.kind])}·${i.ref}`
        : t(REWARD_LABEL[i.kind]);
      if (dice(i)) return `${name}×${diceText(i.q, i.sn ?? "1", i.sd ?? "10000")}${t("econ.shared.dice")}`;
      return NO_QTY.includes(i.kind) ? name : `${name}×${i.qty}`;
    })
    .join(" + ");
}

const DIGITS = /^(0|[1-9][0-9]*)$/;
/** q × sn/sd 骰的十进制展示（最多 4 位小数，除不尽的标 ≈）；全程 bigint，不经浮点 */
export function diceText(q: string | null | undefined, sn: string | null | undefined, sd: string | null | undefined): string {
  if (!q || !sn || !sd || !DIGITS.test(q) || !DIGITS.test(sn) || !DIGITS.test(sd) || sd === "0") return "—";
  const num = BigInt(q) * BigInt(sn), den = BigInt(sd), scaled = (num * 10_000n) / den;
  const exact = (num * 10_000n) % den === 0n;
  const frac = (scaled % 10_000n).toString().padStart(4, "0").replace(/0+$/, "");
  return `${exact ? "" : "≈"}${scaled / 10_000n}${frac ? `.${frac}` : ""}`;
}

let seq = 1;
export const newReward = (levelId: number): Reward => ({
  id: `rw${seq++}`,
  levelId,
  name: "New Reward",
  i18n: {},
  items: [{ kind: "money", qty: null }],
});

/** 某关某类奖励的合计；未配置返回 null，好和「配了 0」区分开 */
export function qtyOf(rw: Reward | undefined, kind: RewardKind, ref?: string): number | null {
  if (!rw) return null;
  // 传 ref 就只算这一张卡的数量——总览要显示「卡名 × 张数」，合计数在这里是错的
  const hit = rw.items.filter((i) => i.kind === kind && i.qty != null && (ref == null || i.ref === ref));
  return hit.length ? hit.reduce((a, i) => a + (i.qty ?? 0), 0) : null;
}

/** 某关某类奖励引用的对象名，用于列表里显示「发了哪几张卡」 */
export function refsOf(rw: Reward | undefined, kind: RewardKind): string[] {
  return (rw?.items ?? []).filter((i) => i.kind === kind && i.ref).map((i) => i.ref!) as string[];
}

export const sumKind = (rewards: Reward[], kind: RewardKind) =>
  rewards.reduce((a, r) => a + (qtyOf(r, kind) ?? 0), 0);
